import api from './api';

export interface LegacyClient {
  id: number;
  org_id: number | null;
  login: string | null;
  status: number;
  sex: number | null;
  F: string | null;
  I: string | null;
  O: string | null;
  tel: string | null;
  born_year: number | null;
  growth: string | null;
  expert_id: number | null;
  last_antropometry: string | null;
  last_event: string | null;
  timestamp: string;
}

export interface LegacyMetric {
  id: number;
  date: string;
  client_id: number;
  weight: number | null;
  waist: number | null;
  chest: number | null;
  hips: number | null;
  hand: number | null;
  leg: number | null;
  body_fat: number | null;
  body_water: number | null;
  visceral_fat: number | null;
  abdomen: number | null;
  arm: number | null;
  metabolic_rate: number | null;
  biological_age: number | null;
  muscle_mass: number | null;
  physically_rating: number | null;
  bone_mass: number | null;
  comment: string | null;
  expert_id: number | null;
  timestamp: string;
}

export interface LegacyRoutineEvent {
  id: number;
  client_id: number | null;
  plan_id: number | null;
  datetime: string | null;
  class: number | null;
  event_name_id: number | null;
  event_name: string | null;
  set_id: number | null;
  proteins: string | null;
  fats: string | null;
  carbs: string | null;
  cellulose: string | null;
  kkal: string | null;
  comment: string | null;
  expert_id: number | null;
  datetime_confirm: string | null;
  template_item_id: number | null;
  timestamp: string;
}

export interface LegacyImportResult {
  legacyRows: number;
  attempted: number;
  imported: number;
  skipped: number;
  failed: number;
  skippedDetails: { legacyId: number; date?: string; datetime?: string; field?: string; reason: string }[];
  failedDetails: { legacyId: number; reason: string }[];
}

export interface LegacyMeta {
  total: number;
  page: number;
  limit: number;
}

export interface LegacyPagedResult<T> {
  data: T[];
  meta: LegacyMeta;
}

interface LegacyResponse<T> {
  data: T;
  meta?: LegacyMeta;
}

export const legacyImportService = {
  getClients: async (params: { search?: string; page?: number; limit?: number } = {}): Promise<LegacyPagedResult<LegacyClient>> => {
    const response = await api.get<LegacyResponse<LegacyClient[]>>('/legacy-import/clients', { params });
    return {
      data: Array.isArray(response.data.data) ? response.data.data : [],
      meta: response.data.meta ?? { total: 0, page: 1, limit: params.limit ?? 25 },
    };
  },

  getMetrics: async (legacyClientId: number, params: { page?: number; limit?: number } = {}): Promise<LegacyPagedResult<LegacyMetric>> => {
    const response = await api.get<LegacyResponse<LegacyMetric[]>>('/legacy-import/metrics', {
      params: { legacyClientId, ...params },
    });
    return {
      data: Array.isArray(response.data.data) ? response.data.data : [],
      meta: response.data.meta ?? { total: 0, page: 1, limit: params.limit ?? 20 },
    };
  },

  importMetrics: async (legacyClientId: number, newUserId: number): Promise<LegacyImportResult> => {
    const response = await api.post<LegacyResponse<LegacyImportResult>>('/legacy-import/metrics/import', {
      legacyClientId,
      newUserId,
    });
    return response.data.data;
  },

  rollbackMetrics: async (legacyClientId: number, newUserId: number): Promise<{ deleted: number }> => {
    const response = await api.delete<LegacyResponse<{ deleted: number }>>('/legacy-import/metrics/import', {
      params: { legacyClientId, newUserId },
    });
    return response.data.data;
  },

  importRoutineEvents: async (legacyClientId: number, newUserId: number, newExpertId: number): Promise<LegacyImportResult> => {
    const response = await api.post<LegacyResponse<LegacyImportResult>>('/legacy-import/routine-events/import', {
      legacyClientId,
      newUserId,
      newExpertId,
    });
    return response.data.data;
  },

  rollbackRoutineEvents: async (legacyClientId: number, newUserId: number): Promise<{ deleted: number }> => {
    const response = await api.delete<LegacyResponse<{ deleted: number }>>('/legacy-import/routine-events/import', {
      params: { legacyClientId, newUserId },
    });
    return response.data.data;
  },

  getRoutineEvents: async (legacyClientId: number, params: { page?: number; limit?: number } = {}): Promise<LegacyPagedResult<LegacyRoutineEvent>> => {
    const response = await api.get<LegacyResponse<LegacyRoutineEvent[]>>('/legacy-import/routine-events', {
      params: { legacyClientId, ...params },
    });
    return {
      data: Array.isArray(response.data.data) ? response.data.data : [],
      meta: response.data.meta ?? { total: 0, page: 1, limit: params.limit ?? 50 },
    };
  },
};
